import { apiClient } from '@/lib/api-client';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface Store {
  _id: string;
  name: string;
  description?: string;
  enabled: boolean;
  currency: string;
  storeType: StoreType;
  orderWorkflow: OrderWorkflowStage[];
  entryStatusKey?: string;
  createdAt: string;
  updatedAt: string;
}

export type StoreType = 'restaurant' | 'retail' | 'services' | 'food_kitchen' | 'supplements' | 'clothing' | 'merchandise' | 'general' | 'other';

export const STORE_TYPE_LABELS: Record<StoreType, string> = {
  restaurant: 'Restaurant',
  food_kitchen: 'Food / Kitchen',
  supplements: 'Supplements & Protein',
  clothing: 'Clothing',
  merchandise: 'Merchandise',
  retail: 'Retail',
  services: 'Services',
  general: 'General Store',
  other: 'Other',
};

export interface OrderWorkflowStage {
  key: string;
  label: string;
  color?: string;
  order: number;
  isTerminal?: boolean;
  isCancellation?: boolean;
  allowedNext: string[];
}

export interface ProductCategory {
  _id: string;
  name: string;
  displayOrder: number;
  availabilityMode?: 'before' | 'after';
  availabilityTime?: string;
  createdAt: string;
}

export interface Product {
  _id: string;
  title: string;
  description?: string;
  mainPhoto: string;
  extraPhotos: string[];
  categoryId?: string;
  price: number;
  available: boolean;
  availabilityMode?: 'before' | 'after';
  availabilityTime?: string;
  createdAt: string;
}

export interface OrderItem {
  productId: string;
  title: string;
  price: number;
  quantity: number;
  mainPhoto: string;
}

export type OrderStatus = string;

export interface Order {
  _id: string;
  userId: { _id: string; name: string; email: string; phone?: string } | string;
  items: OrderItem[];
  totalAmount: number;
  status: OrderStatus;
  paymentMethod: 'pickup';
  deliveryMethod: 'pickup';
  notes?: string;
  cancelledBy?: string;
  createdAt: string;
  updatedAt: string;
}

// ─── API Functions ────────────────────────────────────────────────────────────

export const fetchStore = async (): Promise<Store> => {
  const res = await apiClient.get('/store/config');
  return res.data;
};

export const updateStore = async (data: {
  name?: string;
  description?: string;
  enabled?: boolean;
  currency?: string;
  storeType?: StoreType;
  entryStatusKey?: string;
  orderWorkflow?: OrderWorkflowStage[];
}): Promise<Store> => {
  const res = await apiClient.patch('/store/config', data);
  return res.data;
};

export const fetchCategoryPresets = async (storeType: StoreType): Promise<{ presets: string[] }> => {
  const res = await apiClient.get(`/store/category-presets/${storeType}`);
  return res.data;
};

export const fetchCategories = async (): Promise<ProductCategory[]> => {
  const res = await apiClient.get('/store/categories');
  return res.data;
};

export const createCategory = async (data: {
  name: string;
  displayOrder?: number;
  availabilityMode?: string;
  availabilityTime?: string;
}): Promise<ProductCategory> => {
  const res = await apiClient.post('/store/categories', data);
  return res.data;
};

export const updateCategory = async (
  id: string,
  data: { name?: string; displayOrder?: number; availabilityMode?: string; availabilityTime?: string },
): Promise<ProductCategory> => {
  const res = await apiClient.patch(`/store/categories/${id}`, data);
  return res.data;
};

export const deleteCategory = async (id: string): Promise<void> => {
  await apiClient.delete(`/store/categories/${id}`);
};

export const fetchProducts = async (categoryId?: string): Promise<Product[]> => {
  const res = await apiClient.get('/store/products', {
    params: categoryId ? { categoryId } : undefined,
  });
  return res.data;
};

export const fetchProduct = async (id: string): Promise<Product> => {
  const res = await apiClient.get(`/store/products/${id}`);
  return res.data;
};

export const createProduct = async (data: {
  title: string;
  description?: string;
  mainPhoto: string;
  extraPhotos?: string[];
  categoryId?: string;
  price: number;
  available?: boolean;
  availabilityMode?: string;
  availabilityTime?: string;
}): Promise<Product> => {
  const res = await apiClient.post('/store/products', data);
  return res.data;
};

export const updateProduct = async (
  id: string,
  data: Partial<{
    title: string;
    description: string;
    mainPhoto: string;
    extraPhotos: string[];
    categoryId: string;
    price: number;
    available: boolean;
    availabilityMode: string;
    availabilityTime: string;
  }>,
): Promise<Product> => {
  const res = await apiClient.patch(`/store/products/${id}`, data);
  return res.data;
};

export const deleteProduct = async (id: string): Promise<void> => {
  await apiClient.delete(`/store/products/${id}`);
};

export const fetchOrders = async (params?: { status?: OrderStatus }): Promise<Order[]> => {
  const res = await apiClient.get('/store/orders', { params });
  return res.data;
};

export const fetchOrder = async (id: string): Promise<Order> => {
  const res = await apiClient.get(`/store/orders/${id}`);
  return res.data;
};

export const updateOrderStatus = async (
  id: string,
  status: OrderStatus,
): Promise<Order> => {
  const res = await apiClient.patch(`/store/orders/${id}/status`, { status });
  return res.data;
};

// ─── React Query Hooks ────────────────────────────────────────────────────────

export const useStore = () =>
  useQuery({ queryKey: ['store'], queryFn: fetchStore, staleTime: 30_000 });

export const useStoreCategories = () =>
  useQuery({ queryKey: ['store', 'categories'], queryFn: fetchCategories });

export const useStoreProducts = (categoryId?: string) =>
  useQuery({
    queryKey: ['store', 'products', categoryId],
    queryFn: () => fetchProducts(categoryId),
  });

export const useStoreOrders = (params?: {
  status?: OrderStatus;
  refetchInterval?: number;
}) => {
  const { refetchInterval, ...queryParams } = params ?? {};
  return useQuery({
    queryKey: ['store', 'orders', queryParams],
    queryFn: () => fetchOrders(queryParams),
    refetchInterval: refetchInterval ?? false,
    refetchIntervalInBackground: true,
  });
};

export const useUpdateStore = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: updateStore,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store'] }),
  });
};

export const useCreateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'categories'] }),
  });
};

export const useUpdateCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: { name?: string; displayOrder?: number; availabilityMode?: string; availabilityTime?: string } }) =>
      updateCategory(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'categories'] }),
  });
};

export const useDeleteCategory = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteCategory,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'categories'] }),
  });
};

export const useCreateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: createProduct,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'products'] }),
  });
};

export const useUpdateProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: Parameters<typeof updateProduct>[1] }) =>
      updateProduct(id, data),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'products'] }),
  });
};

export const useDeleteProduct = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: deleteProduct,
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'products'] }),
  });
};

export const useCategoryPresets = (storeType?: StoreType) =>
  useQuery({
    queryKey: ['store', 'category-presets', storeType],
    queryFn: () => fetchCategoryPresets(storeType!),
    enabled: !!storeType,
  });

export const useUpdateOrderStatus = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, status }: { id: string; status: OrderStatus }) =>
      updateOrderStatus(id, status),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['store', 'orders'] }),
  });
};
